import template from "./document_scan_start.html";
import en from "./en.translation.json";
import es from "./es.translation.json";
import mixpanel from "mixpanel-browser";
import {
    UPLOAD_PENDING,
    UPLOAD_SUCCESS,
    FACING_MODE_ENVIRONMENT,
    UPLOAD_FAILED,
    UPLOAD_NOT_STARTED,
} from "../../../common/common.constants";

export const OVERLAY_TIMEOUT = 3000;

class DocumentScanStartController {
    /* @ngInject */
    constructor(
        $document,
        ToastService,
        $http,
        $state,
        $window,
        $q,
        CanvasService,
        $translate,
        $timeout,
    ) {
        this.$document = $document;
        this.ToastService = ToastService;
        this.$http = $http;
        this.$state = $state;
        this.$window = $window;
        this.$q = $q;
        this.CanvasService = CanvasService;
        this.$translate = $translate;
        this.$timeout = $timeout;

        this.UPLOAD_PENDING = UPLOAD_PENDING;
        this.UPLOAD_SUCCESS = UPLOAD_SUCCESS;
        this.UPLOAD_FAILED = UPLOAD_FAILED;
        this.UPLOAD_NOT_STARTED = UPLOAD_NOT_STARTED;
        this.cameraAllowed = true;

        this.IDOptions = {};
        this.uploadStatus = UPLOAD_NOT_STARTED;
    }

    openFilePicker() {
        let ctrl = this;
        ctrl.$document[0].getElementById("idImage").click();
    }

    captureVideo() {
        let ctrl = this;
        let options = {
            video: {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                facingMode: FACING_MODE_ENVIRONMENT,
                frameRate: {
                    ideal: 30,
                },
            },
            audio: false,
        };
        ctrl.$q
            .when(ctrl.$window.navigator.mediaDevices.getUserMedia(options))
            .then(
                function (stream) {
                    ctrl.cameraAllowed = true;
                    ctrl.video.srcObject = stream;
                    ctrl.video.play();
                },
                function () {
                    ctrl.cameraAllowed = false;
                },
            );
    }

    openBackupPhoto() {
        let ctrl = this;
        ctrl.usingBackupPhoto = true;
        ctrl.video = ctrl.$document[0].getElementById("video");
        ctrl.captureVideo();
    }

    supportsCapture() {
        let ctrl = this;
        var input = ctrl.$document[0].createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("capture", "user");
        return "capture" in input && input.capture === "user";
    }

    endVideoCapture() {
        let ctrl = this;
        for (let track of ctrl.video.srcObject.getTracks()) {
            track.stop();
        }
    }

    $onDestroy() {
        let ctrl = this;
        if (ctrl.video) {
            ctrl.endVideoCapture();
        }
    }

    handleCameraPhoto(options) {
        let ctrl = this;
        ctrl.uploadStatus = ctrl.UPLOAD_PENDING;

        createImageBitmap(options.file).then((img) => {
            const TARGET_MAX_W = 2000; // never upscale beyond this width
            const TARGET_MAX_H = 2000; // never upscale beyond this height
            const ratio = Math.min(
                TARGET_MAX_W / img.width,
                TARGET_MAX_H / img.height,
                1, // never upscale
            );
            const destW = Math.round(img.width * ratio);
            const destH = Math.round(img.height * ratio);

            let canvas = ctrl.$document[0].getElementById("canvas");
            canvas.width = destW;
            canvas.height = destH;

            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, destW, destH);

            ctrl.CanvasService.getBlob(canvas).then(function (blob) {
                ctrl.upload(blob);
            });
        });
    }

    takePhoto() {
        let ctrl = this;
        ctrl.uploadStatus = ctrl.UPLOAD_PENDING;
        let canvas = ctrl.$document[0].getElementById("canvas");
        let video = ctrl.video;

        // Set image
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        let context = canvas.getContext("2d", { alpha: false });
        video.pause();

        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const offsetHeight = video.offsetHeight;
        const offsetWidth = video.offsetWidth;

        // Calculate aspect ratios
        const videoAspectRatio = videoWidth / videoHeight;
        const displayedAspectRatio = offsetWidth / offsetHeight;

        let cropWidth, cropHeight, cropX, cropY, finalWidth, finalHeight;

        // Determine the cropping dimensions and position
        if (videoAspectRatio > displayedAspectRatio) {
            // Video is wider than the displayed area
            cropHeight = videoHeight;
            cropWidth = cropHeight * displayedAspectRatio;
            cropX = (videoWidth - cropWidth) / 2;
            cropY = 0;
        } else {
            // Video is taller than the displayed area
            cropWidth = videoWidth;
            cropHeight = cropWidth / displayedAspectRatio;
            cropX = 0;
            cropY = (videoHeight - cropHeight) / 2;
        }
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Calculate the horizontal cropping offset (since the video is centered)
        context.drawImage(
            video,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight,
        );

        ctrl.CanvasService.getBlob(canvas).then(function (blob) {
            ctrl.upload(blob);
        });
    }

    takePhotoHandler() {
        let ctrl = this;
        ctrl.IDOptions = {};
        ctrl.uploadStatus = ctrl.UPLOAD_NOT_STARTED;
        ctrl.errorMessage = undefined;

        let clientSupportsCapture = ctrl.supportsCapture();
        if (clientSupportsCapture) {
            ctrl.openFilePicker();
        } else {
            ctrl.openBackupPhoto();
        }
    }

    upload(file) {
        let ctrl = this;
        let fd = new FormData();
        fd.append("file", file);
        fd.append(
            "upload_preset",
            process.env.CUSTOM_DOCUMENT_SCAN_PHOTO_UPLOAD_PRESET,
        );
        fd.append(
            "folder",
            "external_documents/" +
                ctrl.growerSid +
                "/" +
                ctrl.growerMembershipSid,
        );
        fd.append(
            "context",
            `subject=${ctrl.userSid}|grower_membership=${ctrl.growerMembershipSid}|owner=${ctrl.growerSid}`,
        );
        ctrl.$http
            .post("https://api.cloudinary.com/v1_1/harvust/image/upload", fd, {
                headers: { "Content-Type": undefined },
                skipAuthorization: true,
            })
            .then(
                function (res) {
                    let data = res.data;
                    let [valid, toastMessage] = isValidPhoto(data);
                    if (valid) {
                        ctrl.uploadStatus = UPLOAD_SUCCESS;
                        mixpanel.track("Document scanned", {
                            company_sid: ctrl.growerSid,
                            self:
                                ctrl.account.sid === ctrl.growerMembership.user,
                        });
                    } else {
                        ctrl.uploadStatus = UPLOAD_FAILED;
                        ctrl.errorMessage = toastMessage;
                    }
                },
                function (err) {
                    ctrl.ToastService.create("documentScanStart.FAILURE_TOAST");
                    ctrl.$state.reload();
                },
            );
    }
}

export default {
    controller: DocumentScanStartController,
    bindings: {
        account: "<",
        userSid: "<",
        growerSid: "<",
        growerMembershipSid: "<",
        growerMembership: "<",
    },
    template: template,
    name: "documentScanStart",
    translations: { en, es },
};

function isValidPhoto(data) {
    let quality = data.quality_analysis.focus;
    console.log("Image quality: ", quality);
    if (quality < 0.4) {
        return [false, "documentScanStart.POOR_QUALITY_TOAST"];
    } else {
        return [true, undefined];
    }
}
